import { FeedbackNotificationProps } from '@clarke-energia/foton';

export const CustomClipboardFeedbackNotificationContents = (
  onCloseNotification: () => void,
): Record<string, FeedbackNotificationProps> => {
  return {
    COPY_SUCCESS: {
      kind: 'primary',
      label: 'Sucesso',
      message: 'Informações da simulação foram copiadas.',
      onCloseNotification,
    },
    COPY_FAILURE: {
      kind: 'error',
      label: 'Erro',
      message: 'Não foi possível copiar as informações da simulação.',
      onCloseNotification,
    },
    PASTE_SUCCESS: {
      kind: 'primary',
      label: 'Sucesso',
      message: 'Informações da simulação foram inseridas.',
      onCloseNotification,
    },
    PASTE_FAILURE: {
      kind: 'error',
      label: 'Erro',
      message: 'Não foi possível inserir as informações da simulação.',
      onCloseNotification,
    },
  };
};

export const writeUserClipboard = (formValues: string) => {
  return navigator.clipboard.writeText(formValues);
};

export const readUserClipboard = () => {
  return navigator.clipboard.readText();
};

export const setCustomClipboardValue = (formValues: string) => {
  localStorage.setItem('CUSTOM_CLIPBOARD', formValues);
};

export const readCustomClipboardValue = () => {
  return localStorage.getItem('CUSTOM_CLIPBOARD');
};

type FormValues = Record<string, unknown>;

type PastedSimulation = { inputs?: { parsedPayload?: { leadInfo?: { name?: unknown } | null } } };

export type PasteFailureReason = 'not-a-simulation' | 'clipboard-denied' | 'nothing-to-paste';

export type FormValuesToPaste =
  | { values: FormValues; source: 'user-clipboard' | 'in-app-copy' }
  | { values: null; reason: PasteFailureReason };

export const PASTE_FAILURE_MESSAGES: Record<PasteFailureReason, string> = {
  'not-a-simulation': 'A área de transferência não contém uma simulação. Copie a simulação e cole de novo.',
  'clipboard-denied': 'Permita que o navegador acesse a área de transferência para colar a simulação.',
  'nothing-to-paste': 'Erro ao colar o conteúdo no formulário',
};

const CODE_FENCE = /^```[a-zA-Z]*\s*([\s\S]*?)\s*```$/;

const parseFormValues = (text: string | null | undefined): FormValues | null => {
  const content = text
    ?.replace(/^\uFEFF/, '')
    .trim()
    .replace(CODE_FENCE, '$1');
  if (!content) return null;
  try {
    const parsed = JSON.parse(content);
    const parsedPayload = parsed?.inputs?.parsedPayload;
    const isSimulation = Boolean(parsedPayload) && typeof parsedPayload === 'object' && !Array.isArray(parsedPayload);
    return isSimulation ? parsed : null;
  } catch {
    return null;
  }
};

const readUserClipboardText = async (): Promise<{ text: string } | { error: 'denied' | 'unavailable' }> => {
  try {
    return { text: await readUserClipboard() };
  } catch (error) {
    return { error: (error as { name?: string })?.name === 'NotAllowedError' ? 'denied' : 'unavailable' };
  }
};

export const readFormValuesToPaste = async (userClipboardOnly: boolean): Promise<FormValuesToPaste> => {
  const userClipboard = await readUserClipboardText();
  if ('error' in userClipboard && userClipboard.error === 'denied') {
    return { values: null, reason: 'clipboard-denied' };
  }
  const userClipboardText = 'text' in userClipboard ? userClipboard.text : '';
  if (userClipboardText.trim()) {
    const values = parseFormValues(userClipboardText);
    return values ? { values, source: 'user-clipboard' } : { values: null, reason: 'not-a-simulation' };
  }
  const inAppCopy = userClipboardOnly ? null : parseFormValues(readCustomClipboardValue());
  return inAppCopy ? { values: inAppCopy, source: 'in-app-copy' } : { values: null, reason: 'nothing-to-paste' };
};

export const getPastedLeadName = (formValues: FormValues): string | undefined => {
  const leadName = (formValues as PastedSimulation)?.inputs?.parsedPayload?.leadInfo?.name;
  return typeof leadName === 'string' && leadName ? leadName : undefined;
};

export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime()) as unknown as T;
  }

  if (obj instanceof Array) {
    return obj.map((item) => deepClone(item)) as unknown as T;
  }

  if (obj instanceof Object) {
    const clonedObj: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj as T;
  }

  return obj;
};
